import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, Clock, CheckCircle, ArrowRight, Zap, CalendarCheck } from "lucide-react";
import AppHeader from "@/components/navigation/AppHeader";
import AppFooter from "@/components/layout/AppFooter";

const LOGO_URL = "/smartdienstplan-logo.svg";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="landing-page">
      <AppHeader
        homePath="/"
        logoClassName="w-8 h-8"
        brandClassName="font-heading font-semibold text-lg sm:text-xl text-white whitespace-nowrap"
        containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3"
        actions={(
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-gray-300 hover:text-white hover:bg-white/10 px-2.5 sm:px-4"
                data-testid="login-nav-btn"
              >
                Anmelden
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold px-2.5 sm:px-4" data-testid="register-nav-btn">
                <span className="sm:hidden">Starten</span>
                <span className="hidden sm:inline">Kostenlos starten</span>
              </Button>
            </Link>
          </div>
        )}
      />

      <main className="flex-1">
      {/* Hero Section - Light Background */}
      <section className="pt-20 pb-20 px-6 lg:px-8 bg-gradient-to-b from-[#f8fafc] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#17c671]/10 border border-[#17c671]/30 text-[#17c671] text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Einfache Dienstplanung
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#212529] leading-tight mb-6">
                Schichtplanung,<br />
                <span className="text-[#17c671]">die funktioniert</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 max-w-lg leading-relaxed">
                Erstelle Dienstpläne, lade dein Team ein und verwalte Zusagen â€“ alles an einem Ort. 
                Keine komplizierte Software, nur das, was du brauchst.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-[#17c671] hover:bg-[#14a85d] text-white px-8 h-12 text-base font-semibold btn-active" data-testid="hero-cta-btn">
                    Jetzt loslegen
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 h-12 text-base">
                    Ich habe bereits ein Konto
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                <div className="flex items-center justify-center bg-[#212529] rounded-xl p-8">
                  <img 
                    src={LOGO_URL}
                    alt="smartdienstplan Logo" 
                    className="w-40 h-40 object-contain"
                  />
                </div>
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg border border-gray-100 p-4 animate-slide-in stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#17c671]/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#17c671]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#212529]">12 Zusagen</p>
                    <p className="text-xs text-gray-500">für Montag</p>
                  </div>
                </div>
              </div>
              {/* Second floating card */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg border border-gray-100 p-4 animate-slide-in stagger-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#17c671]/10 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#17c671]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#212529]">8 Mitarbeiter</p>
                    <p className="text-xs text-gray-500">aktiv</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Light Cards */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-semibold text-[#212529] mb-4">
              Alles, was du brauchst
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Keine überladene Software â€“ nur die Funktionen, die wirklich helfen.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-[#f8fafc] rounded-xl p-8 border border-gray-100 hover:border-[#17c671]/40 hover:shadow-lg transition-all duration-200 animate-fade-in stagger-1">
              <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center mb-6">
                <CalendarCheck className="w-6 h-6 text-[#17c671]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#212529] mb-3">
                Schichten erstellen
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Erstelle Schichten mit oder ohne Uhrzeit, definiere Positionen und lege fest, wie viele Mitarbeiter du brauchst.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-[#f8fafc] rounded-xl p-8 border border-gray-100 hover:border-[#17c671]/40 hover:shadow-lg transition-all duration-200 animate-fade-in stagger-2">
              <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-[#17c671]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#212529] mb-3">
                Team einladen
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Teile einen Einladungs-Link und deine Mitarbeiter können sich registrieren und sofort Zusagen machen.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-[#f8fafc] rounded-xl p-8 border border-gray-100 hover:border-[#17c671]/40 hover:shadow-lg transition-all duration-200 animate-fade-in stagger-3">
              <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-[#17c671]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#212529] mb-3">
                Übersicht behalten
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Sieh auf einen Blick, welche Schichten besetzt sind und wo noch Personal fehlt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-[#f8fafc]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-semibold text-[#212529] mb-4">
            Bereit, loszulegen?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Erstelle deinen ersten Dienstplan in wenigen Minuten. Kostenlos und ohne Kreditkarte.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-[#17c671] hover:bg-[#14a85d] text-white px-10 h-12 text-base font-semibold btn-active animate-pulse-green" data-testid="cta-register-btn">
              Kostenlos registrieren
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
      </main>

      <AppFooter />
    </div>
  );
};

export default LandingPage;


